import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { formatDateTime } from './data-helpers';
import { HISTORY_CUTOFF_LABEL, REMOVED_TEMPLATE_FIELDS_MESSAGE } from './history-helpers';

class HistoryCard extends LitElement {
    static get properties() {
        return {
            historyRecord: {
                type: Object,
            },
            fieldColumnWidth: {
                type: String,
            },
        };
    }

    constructor() {
        super();
        this.fieldColumnWidth = '180px';
    }

    static get styles() {
        return [
            css`
                :host {
                    display: block;
                }

                .history-row {
                    border: 1px solid var(--app-secondary-color);
                    border-radius: 8px;
                    padding: 14px;
                    background: var(--app-background-color);
                }

                .history-row.current {
                    border-color: var(--app-primary-color);
                    box-shadow: inset 0 0 0 1px var(--app-primary-color);
                }

                .history-row.initial {
                    border-color: var(--app-success-color);
                    box-shadow: inset 0 0 0 1px var(--app-success-color);
                }

                .history-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 8px;
                    margin-bottom: 10px;
                }

                .history-header-main {
                    min-width: 0;
                    flex: 1;
                }

                .history-header-badges {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    gap: 6px;
                    flex-shrink: 0;
                }

                .history-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1f2937;
                }

                .history-meta {
                    font-size: 12px;
                    color: #4b5563;
                }

                .history-badge {
                    font-size: 11px;
                    font-weight: 600;
                    color: var(--app-primary-color);
                    background: var(--app-primary-light-color);
                    border-radius: 999px;
                    padding: 3px 8px;
                }

                .history-badge.initial {
                    color: var(--app-success-color);
                    background: var(--app-success-color-hover);
                }

                .history-badge.current {
                    color: var(--app-on-primary-color);
                    background: var(--app-primary-color);
                }

                .fields-table {
                    width: 100%;
                    border-collapse: collapse;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    overflow: hidden;
                }

                .fields-table col.field-col {
                    width: var(--history-field-col-width, 180px);
                }

                .fields-table col.changes-col {
                    width: auto;
                }

                .fields-table th {
                    text-align: left;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                    color: #475569;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 8px 10px;
                }

                .fields-table td {
                    font-size: 13px;
                    color: #1f2937;
                    padding: 8px 10px;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: top;
                    overflow-wrap: anywhere;
                }

                .fields-table tbody tr:last-child td {
                    border-bottom: 0;
                }

                .fields-table tbody tr:nth-child(odd) td {
                    background: #ffffff;
                }

                .fields-table tbody tr:nth-child(even) td {
                    background: #f8fafc;
                }

                .field-name {
                    font-weight: 600;
                    color: #0f172a;
                }

                .field-change {
                    color: #1f2937;
                }

                .field-change-from {
                    color: #92400e;
                    text-decoration: line-through;
                }

                .field-change-arrow {
                    color: #64748b;
                    margin: 0 6px;
                }

                .field-change-to {
                    font-weight: 600;
                    color: #0f172a;
                }

                .history-footnote {
                    margin-top: 10px;
                    font-style: italic;
                }

                @media (max-width: 640px) {
                    .history-header {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .history-header-badges {
                        justify-content: flex-start;
                    }
                }
            `,
        ];
    }

    renderTrackedField(field, hasChanged = false) {
        const fieldLabel = field.displayKey || field.path;
        return html`
            <tr>
                <td class="field-name">${fieldLabel}</td>
                <td class="field-change">
                    ${hasChanged
                        ? html`
                              <span class="field-change-from">${field.previousValue}</span>
                              <span class="field-change-arrow">&rarr;</span>
                              <span class="field-change-to">${field.currentValue}</span>
                          `
                        : html`<span class="field-change-to">${field.currentValue}</span>`}
                </td>
            </tr>
        `;
    }

    renderHistoryBadges({ isCurrentRecord, isInitialRecord, changedCount }) {
        return html`
            ${isCurrentRecord
                ? html`<span class="history-badge current">Current Record</span>`
                : ''}
            ${changedCount > 0 && !isInitialRecord
                ? html`<span class="history-badge">${changedCount} changed</span>`
                : ''}
            ${isInitialRecord
                ? html`<span class="history-badge initial">Initial Record</span>`
                : ''}
        `;
    }

    render() {
        const { historyRecord } = this;
        if (!historyRecord?.historyItem) {
            return '';
        }
        const {
            historyItem,
            isCurrentRecord,
            isInitialRecord,
            changedFields = [],
            changedCount,
            showHistoryCutoffFootnote,
        } = historyRecord;

        const hasPreviousRecord = !isInitialRecord;

        const classes = classMap({
            'history-row': true,
            current: isCurrentRecord,
            initial: isInitialRecord && !isCurrentRecord,
        });

        const updatedByString = historyItem.updated_by_display_name
            ? `${historyItem.updated_by_display_name} (${historyItem.updated_by_username})`
            : 'System';

        const titleDate = `${formatDateTime(historyItem.updated)}`;

        return html`
            <div class="${classes}" style=${`--history-field-col-width: ${this.fieldColumnWidth}`}>
                <div class="history-header">
                    <div class="history-header-main">
                        <span class="history-title">${titleDate}</span>
                        <span class="history-meta">by ${updatedByString}</span>
                    </div>
                    <div class="history-header-badges">
                        ${this.renderHistoryBadges({
                            isCurrentRecord,
                            isInitialRecord,
                            changedCount,
                        })}
                    </div>
                </div>
                ${changedCount
                    ? html`<table class="fields-table">
                          <colgroup>
                              <col class="field-col" />
                              <col class="changes-col" />
                          </colgroup>
                          <thead>
                              <tr>
                                  <th>Field</th>
                                  <th>Changes</th>
                              </tr>
                          </thead>
                          <tbody>
                              ${changedFields.map((field) =>
                                  this.renderTrackedField(field, hasPreviousRecord)
                              )}
                          </tbody>
                      </table>`
                    : html`<div class="history-meta">
                          ${REMOVED_TEMPLATE_FIELDS_MESSAGE}
                      </div>`}
                ${showHistoryCutoffFootnote
                    ? html`<div class="history-meta history-footnote">
                          * Changes before ${HISTORY_CUTOFF_LABEL} are not available.
                      </div>`
                    : ''}
            </div>
        `;
    }
}

customElements.define('history-card', HistoryCard);
